// «Учёба за рубежом» без кабинета — адреса, словари и тексты гостевого входа.
//
// Без тяжёлых импортов: файл читают и экран (браузер), и серверные функции,
// и проверка scripts/verify-abroad-guest.mts. Расчёт направлений по архетипам
// живёт отдельно (guest-directions.ts) — он тянет данные профтеста, которым
// в браузере делать нечего.
//
// Тексты AZ/RU/EN, взятые из ТЗ 09.10.2026 в кавычках, — дословно; проверка
// сверяет их буква в букву. AZ — на вычитку носителем.

export type GuestLang = "az" | "ru" | "en";
export const GUEST_LANGS: readonly GuestLang[] = ["az", "ru", "en"];

// ── адреса ────────────────────────────────────────────────────────────

/** Экран старта подбора без кабинета. Сюда ведут кнопки лендинга и дверь главной. */
export const ABROAD_GUEST_START_PATH: Record<GuestLang, string> = {
  ru: "/study-abroad/start",
  az: "/az/xaricde-tehsil/start",
  en: "/en/study-abroad/start",
};

/**
 * Ссылка «Продолжить анкету» — старт с секретом во фрагменте (#run=…).
 * Фрагмент не уходит на сервер и не пишется в логи, а страница старта
 * читает его, кладёт токен в хранилище и убирает из адреса.
 */
export function guestContinuePath(lang: GuestLang, token: string): string {
  return `${ABROAD_GUEST_START_PATH[lang]}#run=${token}`;
}
export const GUEST_RUN_HASH_RE = /^#run=([0-9a-f]{48})$/;

/**
 * Вход в подбор из других мест (аудит 12.10): результат мини-теста и его
 * письмо, письмо карьерного отчёта, кабинет без ребёнка. Одна строка на язык.
 */
export const ABROAD_ENTRY_COPY: Record<GuestLang, string> = {
  ru: "Проверьте, куда можно поступить за рубежом — бесплатно →",
  az: "Xaricdə hara qəbul ola biləcəyinizi yoxlayın — pulsuz →",
  en: "Check where you can get in abroad — for free →",
};
export const abroadEntryUrl = (lang: GuestLang): string =>
  `https://yolutap.com${ABROAD_GUEST_START_PATH[lang]}`;

/** Страница результата по секрету прохождения. */
export function guestResultPath(lang: GuestLang, token: string): string {
  const base: Record<GuestLang, string> = {
    ru: "/study-abroad/r/",
    az: "/az/xaricde-tehsil/r/",
    en: "/en/study-abroad/r/",
  };
  return base[lang] + token;
}

/** Карьерный тест — тот же адрес, что у двери «тест» на главной. */
export function careerTestHref(lang: GuestLang): string {
  return `/start?lang=${lang}&pkg=basic`;
}

/**
 * Консультация без кабинета. Кабинетный заказ (/cabinet/edu-consult) требует
 * ребёнка, поэтому гость идёт туда же, куда ведёт кнопка отчёта по умолчанию
 * (CONSULTATION.href = "/coaches"), — на языке страницы.
 */
export const GUEST_CONSULT_HREF: Record<GuestLang, string> = {
  ru: "/coaches",
  az: "/az/coaches",
  en: "/en/coaches",
};

// ── секрет и хранилище вкладки ───────────────────────────────────────

/** access_token: encode(gen_random_bytes(24), 'hex') — 48 знаков. */
export const GUEST_TOKEN_RE = /^[0-9a-f]{48}$/;

/** sessionStorage: токен прохождения — перезагрузка не теряет анкету. */
export const GUEST_RUN_STORAGE_KEY = "yt_abroad_guest_run";

/**
 * sessionStorage: почта для /start, когда человек из поп-апа уходит на
 * карьерный тест. /start подставляет её в пустое поле и сразу удаляет ключ.
 */
export const PREFILL_EMAIL_STORAGE_KEY = "yt_prefill_email";

// ── словари ───────────────────────────────────────────────────────────

export type GuestGrade = "9_10" | "11" | "graduate";
export const GUEST_GRADES: readonly GuestGrade[] = ["9_10", "11", "graduate"];

export const GUEST_ARCHETYPE_CODES = ["A", "I", "S", "K", "L", "E", "P", "B"] as const;
export type GuestArchetype = (typeof GUEST_ARCHETYPE_CODES)[number];

/**
 * Имена архетипов — КОПИЯ из банков вопросов профтеста (getArchetypeName,
 * src/lib/yolutap/loader.ts). Здесь копия, потому что loader тянет все банки
 * вопросов целиком, а поп-апу нужны восемь строк. Копию стережёт
 * scripts/verify-abroad-guest.mts: правят имя там — сборка падает, пока
 * копия не обновлена.
 */
export const GUEST_ARCHETYPE_NAMES: Record<GuestArchetype, Record<GuestLang, string>> = {
  A: { az: "Analitik", ru: "Аналитик", en: "Analyst" },
  I: { az: "Tədqiqatçı", ru: "Исследователь", en: "Explorer" },
  S: { az: "Yaradıcı", ru: "Созидатель", en: "Creator" },
  K: { az: "Kommunikator", ru: "Коммуникатор", en: "Communicator" },
  L: { az: "Lider-təşkilatçı", ru: "Лидер-организатор", en: "Leader-Organizer" },
  E: { az: "Empat", ru: "Эмпат", en: "Empath" },
  P: { az: "Praktik-eksperimentator", ru: "Практик-экспериментатор", en: "Practical Experimenter" },
  B: { az: "Sahibkar", ru: "Предприниматель", en: "Entrepreneur" },
};

/**
 * Класс со старта → ответ на q_current_grade («В каком ты сейчас классе?»).
 *
 * Вопросы анкеты не меняются, подставляется только ответ, и только там, где он
 * однозначен. «9–10» — НЕ подставляется: это два варианта ответа, и выбрать
 * один из них за семью значило бы выдумать.
 */
export function gradePrefill(grade: GuestGrade): string | null {
  if (grade === "11") return "11";
  if (grade === "graduate") return "уже окончил(а) школу";
  return null;
}

/** Приёмочные прохождения: имя начинается с TEST, регистр не важен. */
export function isTestName(name: string | null | undefined): boolean {
  return /^test/i.test((name ?? "").trim());
}

/**
 * Имя и фамилия ученика на старте — одна проверка для браузера и сервера.
 * После обрезки пробелов минимум 2 символа; буквы любого алфавита (с
 * диакритикой), дефис, апостроф и пробел — «Nər-Bəy», «O’Neil», «Анна Мария».
 */
export const GUEST_NAME_MIN = 2;
export const GUEST_NAME_MAX = 60;
const GUEST_NAME_RE = /^[\p{L}\p{M}'’ -]+$/u;
export function isValidGuestName(value: string | null | undefined): boolean {
  const v = (value ?? "").trim();
  return (
    v.length >= GUEST_NAME_MIN &&
    v.length <= GUEST_NAME_MAX &&
    GUEST_NAME_RE.test(v) &&
    /\p{L}/u.test(v)
  );
}

/** «Имя Фамилия» в колонку name: её читают письма, админка и выгрузки. */
export function guestFullName(first: string, last: string): string {
  return `${first.trim()} ${last.trim()}`.replace(/\s+/g, " ");
}

/**
 * Сколько занимает анкета — решение владельца 12.10: «около 20 минут» (было
 * «7–10 минут»: 31 обязательный вопрос в восьми блоках столько не занимает).
 */
export const GUEST_DURATION: Record<GuestLang, string> = {
  ru: "около 20 минут",
  az: "təxminən 20 dəqiqə",
  en: "about 20 minutes",
};

/** Версия текста согласия на старте. Переписали текст — подняли версию. */
export const GUEST_CONSENT_VERSION = "edu-guest-2026-10-17";

// ── тексты ────────────────────────────────────────────────────────────

export type GuestStartCopy = {
  title: string;
  subtitle: string;
  langHead: string;
  langHint: string;
  langNames: Record<GuestLang, string>;
  gradeHead: string;
  grades: Record<GuestGrade, string>;
  contactsHead: string;
  firstNameLabel: string;
  lastNameLabel: string;
  emailLabel: string;
  emailHint: string;
  phoneLabel: string;
  phoneHint: string;
  consentPre: string;
  consentLink: string;
  consentPost: string;
  /** Обязательная галочка связи (17.10.2026) — рядом с правилами, пишется в contact_consent_at. */
  contactConsent: string;
  errContactConsent: string;
  schoolLabel: string;
  schoolPick: string;
  schoolOther: string;
  schoolOtherPh: string;
  countryLabel: string;
  submit: string;
  busy: string;
  errGrade: string;
  errEmail: string;
  errFirstName: string;
  errLastName: string;
  errConsent: string;
  errRate: string;
  errClosed: string;
  errGeneric: string;
};

export const GUEST_START_COPY: Record<GuestLang, GuestStartCopy> = {
  az: {
    title: "Universitetləri pulsuz yoxla",
    subtitle: `Pulsuz · ${GUEST_DURATION.az} · nəticə dərhal və e-poçta`,
    langHead: "Dil",
    langHint: "Anketin və nəticənin dili",
    langNames: { az: "Azərbaycan", ru: "Русский", en: "English" },
    gradeHead: "Sinif",
    grades: { "9_10": "9–10 sinif", "11": "11 sinif", graduate: "Məzun / tələbə" },
    contactsHead: "Əlaqə",
    firstNameLabel: "Şagirdin adı",
    lastNameLabel: "Şagirdin soyadı",
    emailLabel: "E-poçt",
    emailHint: "nəticə ora gələcək",
    phoneLabel: "Telefon / WhatsApp (istəyə görə)",
    phoneHint: "qəbulda kömək etməyimizi istəyirsinizsə",
    consentPre: "Mən ",
    consentLink: "məlumatların emalı siyasəti",
    consentPost:
      " ilə razıyam və nəticənin göstərilən e-poçta göndəriləcəyini başa düşürəm. 18 yaşım tamamdırsa, razılığı öz adımdan verirəm; iştirakçı 18 yaşından kiçikdirsə, onun valideyni və ya qanuni nümayəndəsi kimi razılıq verirəm.",
    contactConsent: "Yolutap nəticələr barədə mənimlə əlaqə saxlaya bilər (pulsuz məsləhət)",
    errContactConsent: "Davam etmək üçün əlaqə razılığını qeyd edin.",
    schoolLabel: "Məktəb",
    schoolPick: "— siyahıdan seçin —",
    schoolOther: "Digər məktəb",
    schoolOtherPh: "Məktəbin adı",
    countryLabel: "Yaşadığınız ölkə",
    submit: "Başla",
    busy: "Gözləyin…",
    errGrade: "Sinfi seçin.",
    errEmail: "E-poçt ünvanını düzgün yazın.",
    errFirstName: "Adı yazın.",
    errLastName: "Soyadı yazın.",
    errConsent: "Davam etmək üçün razılıq qutusunu işarələyin.",
    errRate: "Bu ünvandan çox cəhd olub. Bir saatdan sonra yenidən yoxlayın və ya bizə yazın: hello@yolutap.com",
    errClosed: "Universitet seçimi hələ açılmayıb. Tezliklə yenidən baxın.",
    errGeneric: "Başlamaq alınmadı. Yenidən cəhd edin.",
  },
  ru: {
    title: "Проверить вузы бесплатно",
    subtitle: `Бесплатно · ${GUEST_DURATION.ru} · результат сразу и на почту`,
    langHead: "Язык",
    langHint: "Язык анкеты и результата",
    langNames: { az: "Azərbaycan", ru: "Русский", en: "English" },
    gradeHead: "Класс",
    grades: { "9_10": "9–10 класс", "11": "11 класс", graduate: "Выпускник / студент" },
    contactsHead: "Контакты",
    firstNameLabel: "Имя ученика",
    lastNameLabel: "Фамилия ученика",
    emailLabel: "E-mail",
    emailHint: "туда придёт результат",
    phoneLabel: "Телефон / WhatsApp (необязательно)",
    phoneHint: "если хотите, чтобы мы помогли с поступлением",
    consentPre: "Я согласен(-на) с ",
    consentLink: "политикой обработки данных",
    consentPost:
      " и понимаю, что результат будет отправлен на указанный e-mail. Если мне есть 18 лет — подтверждаю от своего имени; если участнику нет 18 лет — подтверждаю как его родитель или законный представитель.",
    contactConsent: "Yolutap может связаться со мной по результатам (бесплатная консультация)",
    errContactConsent: "Отметьте согласие на связь, чтобы продолжить.",
    schoolLabel: "Школа",
    schoolPick: "— выберите из списка —",
    schoolOther: "Другая школа",
    schoolOtherPh: "Название школы",
    countryLabel: "Страна проживания",
    submit: "Начать",
    busy: "Секунду…",
    errGrade: "Выберите класс.",
    errEmail: "Укажите корректный e-mail.",
    errFirstName: "Укажите имя.",
    errLastName: "Укажите фамилию.",
    errConsent: "Чтобы продолжить, отметьте согласие.",
    errRate: "Слишком много попыток с этого адреса. Попробуйте через час или напишите нам: hello@yolutap.com",
    errClosed: "Подбор вузов пока закрыт. Загляните чуть позже.",
    errGeneric: "Не получилось начать. Попробуйте ещё раз.",
  },
  en: {
    title: "Check universities for free",
    subtitle: `Free · ${GUEST_DURATION.en} · results instantly and by email`,
    langHead: "Language",
    langHint: "Language of the questionnaire and results",
    langNames: { az: "Azərbaycan", ru: "Русский", en: "English" },
    gradeHead: "Grade",
    grades: { "9_10": "Grade 9–10", "11": "Grade 11", graduate: "Graduate / student" },
    contactsHead: "Contacts",
    firstNameLabel: "Student’s first name",
    lastNameLabel: "Student’s last name",
    emailLabel: "Email",
    emailHint: "your results will be sent there",
    phoneLabel: "Phone / WhatsApp (optional)",
    phoneHint: "if you’d like us to help with admission",
    consentPre: "I agree to the ",
    consentLink: "data processing policy",
    consentPost:
      " and understand that the results will be sent to the indicated email. If I am 18 or older, I confirm on my own behalf; if the participant is under 18, I confirm as their parent or legal guardian.",
    contactConsent: "Yolutap may contact me about my results (free consultation)",
    errContactConsent: "Please tick the contact consent to continue.",
    schoolLabel: "School",
    schoolPick: "— choose from the list —",
    schoolOther: "Other school",
    schoolOtherPh: "School name",
    countryLabel: "Country of residence",
    submit: "Start",
    busy: "One moment…",
    errGrade: "Please choose the grade.",
    errEmail: "Please enter a valid email.",
    errFirstName: "Please enter the first name.",
    errLastName: "Please enter the last name.",
    errConsent: "Please tick the consent box to continue.",
    errRate: "Too many attempts from this address. Please try again in an hour or write to us: hello@yolutap.com",
    errClosed: "University matching isn’t open yet. Please check back soon.",
    errGeneric: "Couldn’t start. Please try again.",
  },
};

export type GuestPopupCopy = {
  question: string;
  yes: string;
  no: string;
  primary: string;
  secondary: string;
  choose: string;
  hint: string;
  continue: string;
  dontRemember: string;
  recommend: string;
  takeTest: string;
  later: string;
};

export const GUEST_POPUP_COPY: Record<GuestLang, GuestPopupCopy> = {
  az: {
    question: "Yolutap karyera testindən keçmisiniz?",
    yes: "Bəli",
    no: "Xeyr",
    primary: "Əsas arxetip",
    secondary: "İkinci arxetip (istəyə görə)",
    choose: "Seçin",
    hint: "Arxetiplər hesabatın 2-ci səhifəsindədir",
    continue: "Davam et",
    dontRemember: "Xatırlamıram",
    recommend:
      "Tövsiyə edirik: əvvəlcə karyera testindən keçin — universitet seçimi uşağın güclü tərəflərinə əsaslananda daha dəqiq olur.",
    takeTest: "Testdən keç",
    later: "Sonra, indi davam et",
  },
  ru: {
    question: "Вы проходили карьерный тест Yolutap?",
    yes: "Да",
    no: "Нет",
    primary: "Ведущий архетип",
    secondary: "Второй архетип (необязательно)",
    choose: "Выберите",
    hint: "Архетипы — на 2-й странице отчёта",
    continue: "Продолжить",
    dontRemember: "Не помню",
    recommend:
      "Рекомендуем сначала пройти карьерный тест — подбор вуза точнее, когда он опирается на сильные стороны ребёнка.",
    takeTest: "Пройти тест",
    later: "Позже, продолжить подбор",
  },
  en: {
    question: "Have you taken the Yolutap career test?",
    yes: "Yes",
    no: "No",
    primary: "Leading archetype",
    secondary: "Second archetype (optional)",
    choose: "Choose",
    hint: "Archetypes are on page 2 of the report",
    continue: "Continue",
    dontRemember: "I don’t remember",
    recommend:
      "We recommend taking the career test first — university matching is more accurate when it’s based on the child’s strengths.",
    takeTest: "Take the test",
    later: "Later, continue matching",
  },
};

export type GuestFlowCopy = {
  loading: string;
  ruOnlyNote: string | null;
  computing: string;
  computingHint: string;
  failed: string;
  retry: string;
};

export const GUEST_FLOW_COPY: Record<GuestLang, GuestFlowCopy> = {
  az: {
    loading: "Yüklənir…",
    // Показывается, пока перевод анкеты на язык неполный (покрытие < 100%,
    // questionnaireCoverage). Переведут всё — строка исчезнет сама.
    ruOnlyNote: "Anket hələ tam tərcümə olunmayıb — bəzi suallar rus dilindədir.",
    computing: "Variantları hesablayırıq…",
    computingHint: "Bir neçə saniyə çəkir. Səhifəni yeniləmək lazım deyil.",
    failed: "Hesablama alınmadı. Cavablarınız yadda saxlanılıb — yenidən cəhd edin.",
    retry: "Yenidən cəhd et",
  },
  ru: {
    loading: "Загрузка…",
    ruOnlyNote: null,
    computing: "Считаем варианты…",
    computingHint: "Это занимает несколько секунд. Обновлять страницу не нужно.",
    failed: "Расчёт не удался. Ответы сохранены — попробуйте ещё раз.",
    retry: "Попробовать снова",
  },
  en: {
    loading: "Loading…",
    ruOnlyNote: "The questionnaire isn’t fully translated yet — some questions are in Russian.",
    computing: "Calculating your options…",
    computingHint: "It takes a few seconds. No need to refresh the page.",
    failed: "The calculation failed. Your answers are saved — please try again.",
    retry: "Try again",
  },
};

export type GuestResultCopy = {
  directionsHead: string;
  fitsBadge: string;
  testBanner: string;
  /** Кнопка «Поделиться» у отчёта (Web Share API, иначе копирование ссылки). */
  share: string;
  copyLink: string;
  copied: string;
  shareHint: string;
  notFound: string;
  notFinished: string;
  startAgain: string;
  continueRun: string;
  loading: string;
};

export const GUEST_RESULT_COPY: Record<GuestLang, GuestResultCopy> = {
  az: {
    directionsHead: "Profilinizə uyğun istiqamətlər",
    fitsBadge: "Profilinizə uyğun",
    testBanner: "Karyera testindən keçin — nəticəni profilə görə dəqiqləşdirək",
    share: "Paylaş",
    copyLink: "Keçidi kopyala",
    copied: "Keçid kopyalandı",
    shareHint: "Keçiddə uşağın adı ilə hesabat görünür",
    notFound: "Nəticə tapılmadı. Keçidi yoxlayın və ya yenidən başlayın.",
    notFinished: "Anket hələ tamamlanmayıb.",
    startAgain: "Yenidən başla",
    continueRun: "Davam et",
    loading: "Yüklənir…",
  },
  ru: {
    directionsHead: "Направления под ваш профиль",
    fitsBadge: "Под ваш профиль",
    testBanner: "Пройдите карьерный тест — уточним подбор под профиль",
    share: "Поделиться",
    copyLink: "Скопировать ссылку",
    copied: "Ссылка скопирована",
    shareHint: "По ссылке виден отчёт с именем ребёнка",
    notFound: "Результат не найден. Проверьте ссылку или начните заново.",
    notFinished: "Анкета ещё не завершена.",
    startAgain: "Начать заново",
    continueRun: "Продолжить",
    loading: "Загрузка…",
  },
  en: {
    directionsHead: "Directions that fit your profile",
    fitsBadge: "Fits your profile",
    testBanner: "Take the career test — we’ll refine the matching to your profile",
    share: "Share",
    copyLink: "Copy link",
    copied: "Link copied",
    shareHint: "The link shows the report with the child’s name",
    notFound: "Result not found. Check the link or start again.",
    notFinished: "The questionnaire isn’t finished yet.",
    startAgain: "Start again",
    continueRun: "Continue",
    loading: "Loading…",
  },
};

/** <head> старта и результата: на языке страницы, без индексации (как /start). */
export const GUEST_HEAD_COPY: Record<
  GuestLang,
  {
    start: { title: string; description: string };
    result: { title: string; description: string };
    /** og:description старта и отчёта — строка модуля из design/PRODUCT.md. */
    og: string;
  }
> = {
  az: {
    og: "Xaricdəki universitetlərin Azərbaycan attestatı, dil imtahanları və ailə büdcəsi üzrə pulsuz yoxlanışı. Yalnız rəsmi mənbələr, qəbul vədi yoxdur.",
    start: {
      title: "Universitetləri pulsuz yoxla — Yolutap",
      description: `Xaricdə hansı universitetlərə qəbul ola biləcəyinizi pulsuz yoxlayın: təxminən 20 dəqiqəlik anket, nəticə dərhal və e-poçtunuza.`,
    },
    result: {
      title: "Universitet seçiminin nəticəsi — Yolutap",
      description:
        "Xaricdə universitet seçiminin nəticəsi: hara və hansı şərtlərlə qəbul olmaq mümkündür.",
    },
  },
  ru: {
    og: "Бесплатная проверка вузов за рубежом по азербайджанскому аттестату, языковым экзаменам и бюджету семьи. Только официальные источники, без обещаний поступления.",
    start: {
      title: "Проверить вузы бесплатно — Yolutap",
      description: `Проверьте бесплатно, в какие вузы за рубежом можно поступить: анкета примерно на 20 минут, результат сразу и на почту.`,
    },
    result: {
      title: "Результат подбора вузов — Yolutap",
      description: "Результат подбора вузов за рубежом: куда и на каких условиях можно поступить.",
    },
  },
  en: {
    og: "A free check of universities abroad against an Azerbaijani school certificate, language exams and the family budget. Official sources only, no promises of admission.",
    start: {
      title: "Check universities for free — Yolutap",
      description: `Check for free which universities abroad you can get into: a questionnaire of about 20 minutes, results instantly and by email.`,
    },
    result: {
      title: "Your university matching results — Yolutap",
      description:
        "Your university matching results: where you can get in abroad and on what terms.",
    },
  },
};

/**
 * Письмо сразу после старта: ссылка «Продолжить анкету», которая открывает
 * анкету с того же места на любом устройстве. `{name}` подставляется.
 */
export const GUEST_CONTINUE_EMAIL_COPY: Record<
  GuestLang,
  { subject: string; hello: string; helloNamed: string; body: string; keep: string; button: string }
> = {
  az: {
    subject: "Anketə qayıtmaq üçün keçid — Yolutap",
    hello: "Salam!",
    helloNamed: "Salam, {name}!",
    body: "Universitet seçimi anketini başladınız. Ara versəniz, bu keçidlə istənilən cihazdan elə həmin yerdən davam edə bilərsiniz.",
    keep: "Keçidi heç kimə göndərməyin — o, sizin anketinizi açır.",
    button: "Anketə davam et",
  },
  ru: {
    subject: "Ссылка, чтобы вернуться к анкете — Yolutap",
    hello: "Здравствуйте!",
    helloNamed: "Здравствуйте, {name}!",
    body: "Вы начали анкету подбора вузов. Если прервётесь — по этой ссылке продолжите с того же места на любом устройстве.",
    keep: "Не пересылайте ссылку — она открывает вашу анкету.",
    button: "Продолжить анкету",
  },
  en: {
    subject: "A link to get back to your questionnaire — Yolutap",
    hello: "Hello!",
    helloNamed: "Hello, {name}!",
    body: "You’ve started the university matching questionnaire. If you stop, this link lets you continue from the same place on any device.",
    keep: "Don’t forward this link — it opens your questionnaire.",
    button: "Continue the questionnaire",
  },
};

/** Письмо человеку — на языке результата. `{name}` и `{link}` подставляются. */
export const GUEST_EMAIL_COPY: Record<
  GuestLang,
  { subject: string; hello: string; helloNamed: string; body: string; keep: string; button: string }
> = {
  az: {
    subject: "Universitet seçiminin nəticəsi — Yolutap",
    hello: "Salam!",
    helloNamed: "Salam, {name}!",
    body: "Universitet seçiminin nəticəsi hazırdır.",
    keep: "Keçidi saxlayın — nəticə həmişə orada olacaq.",
    button: "Nəticəyə bax",
  },
  ru: {
    subject: "Результат подбора вузов — Yolutap",
    hello: "Здравствуйте!",
    helloNamed: "Здравствуйте, {name}!",
    body: "Результат подбора вузов готов.",
    keep: "Сохраните ссылку — результат всегда будет по ней.",
    button: "Открыть результат",
  },
  en: {
    subject: "Your university matching results — Yolutap",
    hello: "Hello!",
    helloNamed: "Hello {name},",
    body: "Your university matching results are ready.",
    keep: "Keep this link — your results will always be there.",
    button: "Open the results",
  },
};

// ── страна проживания (17.10.2026) ──────────────────────────────────

/** Страны проживания для старта: ISO-код → подпись. По умолчанию Азербайджан, «other» — любая другая. */
export const RESIDENCE_COUNTRIES: { code: string; label: Record<GuestLang, string> }[] = [
  { code: "AZ", label: { az: "Azərbaycan", ru: "Азербайджан", en: "Azerbaijan" } },
  { code: "TR", label: { az: "Türkiyə", ru: "Турция", en: "Türkiye" } },
  { code: "RU", label: { az: "Rusiya", ru: "Россия", en: "Russia" } },
  { code: "GE", label: { az: "Gürcüstan", ru: "Грузия", en: "Georgia" } },
  { code: "UA", label: { az: "Ukrayna", ru: "Украина", en: "Ukraine" } },
  { code: "KZ", label: { az: "Qazaxıstan", ru: "Казахстан", en: "Kazakhstan" } },
  { code: "UZ", label: { az: "Özbəkistan", ru: "Узбекистан", en: "Uzbekistan" } },
  { code: "IR", label: { az: "İran", ru: "Иран", en: "Iran" } },
  { code: "AE", label: { az: "BƏƏ", ru: "ОАЭ", en: "UAE" } },
  { code: "DE", label: { az: "Almaniya", ru: "Германия", en: "Germany" } },
  { code: "GB", label: { az: "Böyük Britaniya", ru: "Великобритания", en: "United Kingdom" } },
  { code: "US", label: { az: "ABŞ", ru: "США", en: "USA" } },
  { code: "other", label: { az: "Başqa ölkə", ru: "Другая страна", en: "Other country" } },
];
export const RESIDENCE_DEFAULT = "AZ";
export const isResidenceCountry = (v: unknown): v is string => typeof v === "string" && RESIDENCE_COUNTRIES.some((c) => c.code === v);

/** Свободное название школы («Другая»): до стольких знаков. */
export const ORIGIN_SCHOOL_OTHER_MAX = 120;
/** Значение выбора «Другая школа» в списке. */
export const ORIGIN_SCHOOL_OTHER = "other";
